import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import type { DateRange } from "react-day-picker";
import { useTeamAccess } from "../contexts/AuthContext";
import { rangeLabel, rangeLabelAsOf } from "../lib/dayLabel";
import { formatModelLabel } from "../lib/modelLabel";
import {
  formatEstimatedCost,
  formatEstimatedCostCompact,
} from "../lib/modelPricing";
import {
  fetchTeamAgentUsageDaily,
  emptyTotals,
  sumUsage,
  type AgentUsageDailyRow,
  type UsageTotals,
} from "../lib/agentUsage";
import { UsageCostFootnote } from "./UsageCost";
import { getModelColor } from "../lib/modelColors";
import { CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "./ui/chart";

// Defensive cap on the day-scaffold loop below, AND the value NewDashboard.tsx
// feeds into the picker's own `maxRangeDays` prop so the calendar disables
// anything wider before it can even be selected. Exported (not just a local
// backstop) specifically so those two stay the same number — a picker cap
// that quietly disagreed with this component's own loop cap would mean the
// UI implies a wider selection is fine while this chart silently truncates
// it with no indication, which is exactly the kind of silent-clamp we don't
// want (compare hasUnpricedUsage below, which surfaces its own limitation
// instead of just under-reporting).
export const MAX_RANGE_DAYS = 90;

const toDateKey = (d: Date): string =>
  d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");

// ============================================================================
// Component: TokenSpendOverTime
//
// Real substitute for syncsimple-v2's "Token Spend" chart — a stacked bar
// per day, stacked by MODEL (switched from per-AGENT 2026-09-05 — matches
// the Figma reference, which stacks by model, and matches TopModelsUsed's
// existing per-model grouping elsewhere on this dashboard). Each bar segment
// is a LIST-PRICE ESTIMATE in $ — 2026-10-02 (Step 6 of
// docs/TOKEN_COST_ACCURACY_PLAN.md): read from get_team_agent_usage_daily via
// src/lib/agentUsage.ts, priced server-side per usage event (cache tokens
// included) and frozen at ingest, instead of agent_checkin.tokens_* run
// through a client-side rate table. Unpriced usage contributes 0 to the
// chart and is called out below it (UsageCostFootnote) rather than silently
// vanishing; fallback-priced usage is marked the same way.
//
// Each distinct model gets its own color via src/lib/modelColors.ts's
// getModelColor — a deterministic hash of the model id itself, not
// first-encountered order (the old per-agent version's mechanism). Hashing
// means a given model renders as the same color here as it does in
// TopModelsUsed/TopProjects, and stays that color across different date
// ranges, instead of shifting depending on what else happened to be in the
// data first.
//
// ROLE GATING: widened from admin-only to admin-or-manager 2026-09-29 (the
// "revisit deliberately" this comment used to ask for) — same reasoning as
// AgentsCostPanel/TopAgentsByTokens. Grouping is by MODEL rather than
// agent/human, so there's no per-person attribution here to begin with. The
// RPC returns agents the caller can't see in detail with agent_id NULL
// (pre-aggregated server-side); this is a team-wide figure, so those rows
// are counted like any other.
//
// 2026-09-09: took a `date` prop and the 7-day window ended on that day
// instead of always ending "today" — so picking a past date on the
// dashboard showed that week's trend, not this week's. Deliberately NOT
// collapsed to a single day like TopProjects/TopModelsUsed/AgentsCostPanel:
// this is the one dashboard card whose whole point is showing a trend across
// days, and a single-day bar chart has nothing to trend.
//
// 2026-09-13: briefly replaced that borrowed single date with its OWN
// start/end range picker living in this card's own header — reasoning at
// the time was that this is the only dashboard card whose window length is
// meaningful to vary, so it shouldn't need to share the single-day picker
// every other card uses. Reverted 2026-09-14: the team flagged (correctly)
// that two independent date controls on one dashboard page is a real
// hierarchy problem — nothing tells a user which one "wins," and in this
// case neither did; they were genuinely independent. NewDashboard.tsx now
// owns exactly one picker (a start/end range with presets — see
// ui/date-range-picker.tsx) and passes `range` down to this component as a
// prop; the other five widgets (TopModelsUsed, TopProjects,
// NeedsAttentionCard, PeopleAndAgentsPreview, AgentsCostPanel) keep reading
// a derived single date off that same picker's end date, so a plain
// "Today"/"Yesterday" pick behaves exactly as it always did for them.
// ============================================================================

interface TokenSpendOverTimeProps {
  teamId: string;
  /** Owned by NewDashboard.tsx's single dashboard-wide date control now —
   * see the 2026-09-14 note above. This component no longer picks its own. */
  range: DateRange;
  // Pod filter (NewDashboard.tsx's pod dropdown). Safe here regardless of
  // role since this widget is already admin/manager-gated below, so the
  // caller always has real (unmasked) agent_id visibility.
  agentIds?: string[];
}

interface DayRow {
  day: string;
  [modelKey: string]: string | number;
}

const TokenSpendOverTime: React.FC<TokenSpendOverTimeProps> = ({ teamId, range, agentIds }) => {
  const { role, loading: roleLoading } = useTeamAccess(teamId);
  const canViewCosts = role === "owner" || role === "admin" || role === "manager";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DayRow[]>([]);
  const [config, setConfig] = useState<ChartConfig>({});
  const [rangeTotals, setRangeTotals] = useState<UsageTotals>(emptyTotals());

  // NewDashboard.tsx always keeps a complete {from, to} committed, but stay
  // defensive against a caller passing a mid-selection or empty range.
  const rangeStart = range.from ?? new Date();
  const rangeEnd = range.to ?? rangeStart;

  useEffect(() => {
    if (roleLoading) return;
    if (!canViewCosts) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      const startKey = toDateKey(rangeStart);
      const endKey = toDateKey(rangeEnd);

      let usageRows: AgentUsageDailyRow[];
      try {
        // Includes agent_id-null rows: a team-wide total, not per-agent.
        usageRows = await fetchTeamAgentUsageDaily(
          teamId,
          startKey,
          endKey,
          agentIds ? { agentIds } : undefined,
        );
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load token spend:", err);
        setError("Could not load token spend.");
        setLoading(false);
        return;
      }

      if (cancelled) return;

      // Build every calendar day in the selected range so model-free days
      // still appear as zero bars, matching the shape of v2's mock data.
      // Defensively capped at MAX_RANGE_DAYS even though the picker already
      // disables dates beyond that — belt-and-suspenders against ever
      // building a runaway number of bars.
      const days: string[] = [];
      // Normalized to local midnight via the key strings, not compared as
      // raw Date objects — rangeStart/rangeEnd can carry a "now" time
      // component (the default range's `to` is `new Date()`), and comparing
      // those directly against a midnight cursor would silently drop the
      // last day once the time-of-day ordering flipped.
      const cursorDate = new Date(startKey + "T00:00:00");
      const endDate = new Date(endKey + "T00:00:00");
      for (let d = 0; d < MAX_RANGE_DAYS && cursorDate <= endDate; d += 1) {
        days.push(toDateKey(cursorDate));
        cursorDate.setDate(cursorDate.getDate() + 1);
      }

      const rowsByDay = new Map<string, DayRow>(
        days.map((day) => [
          day,
          {
            day: new Date(day + "T00:00:00").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
          },
        ]),
      );

      // Keyed by the raw model string (e.g. "claude-opus-4-5-20250929"), not
      // by agent — this is the actual grouping change. `key`/`label` are
      // assigned once, the first time a given model is seen, and reused for
      // every later row for that same model so its legend entry stays
      // stable across the whole selected range (not reassigned per day).
      // Color isn't derived from this encounter order at all — see the
      // getModelColor call below.
      const modelsSeen = new Map<string, { key: string; label: string }>();
      for (const row of usageRows) {
        const dayRow = rowsByDay.get(row.usageDate);
        if (!dayRow) continue;
        // Unpriced groups (cost_usd null) add nothing to the bar; the
        // footnote below says so instead of the chart quietly under-reporting.
        if (row.costUsd === null) continue;

        const modelId = row.model || "unknown";
        let modelEntry = modelsSeen.get(modelId);
        if (!modelEntry) {
          modelEntry = {
            key: `model_${modelsSeen.size}`,
            label:
              modelId === "unknown"
                ? "Unknown model"
                : formatModelLabel(modelId),
          };
          modelsSeen.set(modelId, modelEntry);
        }

        dayRow[modelEntry.key] =
          ((dayRow[modelEntry.key] as number) ?? 0) + row.costUsd;
      }

      const nextConfig: ChartConfig = {};
      for (const [modelId, modelEntry] of modelsSeen.entries()) {
        nextConfig[modelEntry.key] = {
          label: modelEntry.label,
          color: getModelColor(modelId),
        };
      }

      setData(Array.from(rowsByDay.values()));
      setConfig(nextConfig);
      setRangeTotals(sumUsage(usageRows));
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
    // rangeStart/rangeEnd are new Date objects each render, so depend on
    // their primitive values instead — otherwise this effect would refetch
    // on every render rather than only when the selected range changes.
  }, [teamId, canViewCosts, roleLoading, rangeStart.getTime(), rangeEnd.getTime(), agentIds?.join(",")]);

  const modelKeys = useMemo(() => Object.keys(config), [config]);

  if (roleLoading || !canViewCosts) {
    return null;
  }

  // 2026-09-17: was its own inline sameDay/sameYear formatting (duplicating
  // what dayLabel.ts's rangeLabel/rangeLabelAsOf now do for every other
  // dashboard card). Switched to those shared helpers while fixing the
  // empty-state bug below — this card's own label already collapsed a
  // single-day selection correctly, but the "no activity" message didn't,
  // so picking one day rendered "No agent activity between Sep 8 and Sep 8,
  // 2026." instead of "No agent activity on Sep 8, 2026."
  const rangeStartKey = toDateKey(rangeStart);
  const rangeEndKey = toDateKey(rangeEnd);

  return (
    <div className="h-full">
      <CardHeader>
        <CardTitle className="text-base">Token Spend</CardTitle>
        {/* No picker here anymore — this card follows the dashboard's one
            shared date control (see the 2026-09-14 note above) and just
            states what window that control currently has it showing. */}
        <CardDescription>{rangeLabel(rangeStartKey, rangeEndKey)} — list-price estimate by model.</CardDescription>
      </CardHeader>
      <div className="border-t border-border" />
      <CardContent className="pt-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-[220px] w-full" />
            <Skeleton className="h-3 w-48" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rangeTotals.eventCount === 0 ? (
          <>
            <ChartContainer config={{}} className="aspect-auto h-[220px] w-full">
              <BarChart data={data} margin={{ left: -20 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  fontSize={11}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  domain={[0, (max: number) => Math.max(max, 100)]}
                  tickFormatter={(value: number) => formatEstimatedCostCompact(value)}
                />
              </BarChart>
            </ChartContainer>
            <p className="mt-2 text-sm text-muted-foreground">
              No connector activity {rangeLabelAsOf(rangeStartKey, rangeEndKey)}.
            </p>
          </>
        ) : modelKeys.length === 0 ? (
          <>
            <p className="text-sm text-muted-foreground">
              {rangeTotals.unpricedEventCount.toLocaleString()} unpriced usage
              event{rangeTotals.unpricedEventCount === 1 ? "" : "s"}{" "}
              {rangeLabelAsOf(rangeStartKey, rangeEndKey)} — no list price
              available for the model{rangeTotals.unpricedEventCount === 1 ? "" : "s"} used.
            </p>
          </>
        ) : (
          <>
            <ChartContainer
              config={config}
              className="aspect-auto h-[220px] w-full"
            >
              <BarChart data={data} margin={{ left: -20 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  fontSize={11}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  tickFormatter={(value: number) =>
                    formatEstimatedCostCompact(value)
                  }
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value: number, _name: string, item: any) => (
                        <div className="flex w-full flex-1 items-center justify-between gap-2 leading-none">
                          <div className="flex items-center gap-1.5">
                            <div
                              className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                              style={{ backgroundColor: item?.color }}
                            />
                            <span className="text-muted-foreground">
                              {config[item?.dataKey as string]?.label}
                            </span>
                          </div>
                          <span className="font-mono font-medium tabular-nums text-foreground">
                            {formatEstimatedCost(value)}
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                {modelKeys.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="spend"
                    fill={`var(--color-${key})`}
                    radius={
                      index === 0
                        ? [0, 0, 4, 4]
                        : index === modelKeys.length - 1
                          ? [4, 4, 0, 0]
                          : [0, 0, 0, 0]
                    }
                  />
                ))}
              </BarChart>
            </ChartContainer>
            <UsageCostFootnote totals={rangeTotals} className="mt-2" />
          </>
        )}
      </CardContent>
    </div>
  );
};

export default TokenSpendOverTime;
